/**
 * Le disposizioni delle mappe: UN motore, tre viste, tutte le pagine.
 *
 * Topologia, correlazione del traffico e mappa di un business service
 * disegnano la stessa cosa — nodi a livelli, archi fra loro — e devono offrire
 * le stesse viste: due geometrie divergerebbero alla prima correzione, e la
 * divergenza si vedrebbe come «lo stesso oggetto ha due forme».
 *
 * ## Le tre viste, e il criterio di ciascuna
 *
 * - **gerarchica**: fasce orizzontali per livello, dall'alto in basso. Il
 *   livello è il **criterio** che la pagina sceglie: in topologia il ruolo
 *   dell'oggetto (perimetro → switching → cluster → host → VM → storage), nella
 *   correlazione la distanza dal seme. Dentro una fascia i nodi si ordinano per
 *   baricentro, in più passate su e giù: è ciò che riduce gli incroci. Le fasce
 *   troppo larghe vanno a capo, sfalsate, invece di diventare un filo di
 *   perline largo tre schermi;
 * - **radiale**: anelli attorno a un centro — il nodo scelto, se c'è, con la
 *   distanza in salti; altrimenti il primo livello. Il raggio di ogni anello
 *   cresce con quanti nodi deve contenere, così i nodi non si toccano;
 * - **organica**: a forze, ma **deterministica** e ancorata alle fasce. Parte
 *   dalla gerarchica, avvicina ciò che è collegato e allontana il resto, con una
 *   molla debole che tiene ogni nodo vicino alla sua fascia: la struttura
 *   emerge senza perdere il criterio.
 *
 * ## Perché deterministico
 *
 * Una disposizione che cambia a ogni apertura costringe a ricercare ogni volta
 * il nodo lasciato lì ieri. Qui la posizione dipende solo dai dati: stessa
 * mappa, stessa forma — anche nella vista organica.
 *
 * Le coordinate sono in «pixel a zoom 1»: la tela le trasforma, e le
 * spaziature qui sotto sono pensate per le etichette che ci stanno sotto.
 */

import { t } from '../i18n/lingua'

export type LayoutMappa = 'gerarchico' | 'radiale' | 'organico'

export const LAYOUTS: { key: LayoutMappa; label: () => string; hint: () => string }[] = [
  {
    key: 'gerarchico',
    label: () => t('Gerarchica'),
    hint: () => t('Fasce per livello, dall’alto in basso: il criterio della pagina'),
  },
  {
    key: 'radiale',
    label: () => t('Radiale'),
    hint: () => t('Anelli attorno al centro: la distanza in salti'),
  },
  {
    key: 'organico',
    label: () => t('Organica'),
    hint: () => t('Ciò che è collegato si avvicina, restando nella sua fascia'),
  },
]

export interface NodoDaPosizionare {
  id: string
  /** Il livello del criterio: la fascia nella vista gerarchica. */
  level: number
  /** Per gli spareggi: l'etichetta, così l'ordine è leggibile e stabile. */
  ordine?: string
}

export interface ArcoDaPosizionare {
  src: string
  dst: string
}

export interface Posizione {
  x: number
  y: number
}

/** Una fascia della vista gerarchica: dove sta, per disegnarla dietro i nodi. */
export interface Fascia {
  livello: number
  y0: number
  y1: number
}

/** Un anello della vista radiale: per il livello del criterio o per i salti. */
export interface Anello {
  raggio: number
  /** Dove scrivere il nome: il varco più ampio fra i nodi dell'anello. */
  angoloNome: number
  livello?: number
  salti?: number
}

export interface Disposizione {
  posizioni: Map<string, Posizione>
  fasce: Fascia[]
  anelli?: Anello[]
}

/** Spaziatura orizzontale fra i nodi di una fascia: ci deve stare l'etichetta. */
const PASSO_X = 132
/** Distanza fra una fascia e la successiva. */
const PASSO_FASCIA = 132
/** Distanza fra le righe di una fascia andata a capo. */
const PASSO_RIGA = 108
/** Distanza minima fra anelli. */
const PASSO_ANELLO = 170

const CHIAVE_MEMORIA = 'censo.mappa.layout'

export function leggiLayout(): LayoutMappa {
  try {
    const salvato = localStorage.getItem(CHIAVE_MEMORIA) ?? localStorage.getItem('censo.mappa.layout')
    if (salvato === 'gerarchico' || salvato === 'radiale' || salvato === 'organico') return salvato
    // Le viste di prima: si traducono invece di perdere la scelta.
    if (salvato === 'colonne') return 'gerarchico'
    if (salvato === 'anelli' || salvato === 'anello') return 'radiale'
  } catch {
    // Storage negato: si parte dalla gerarchica e basta.
  }
  return 'gerarchico'
}

export function scriviLayout(layout: LayoutMappa): void {
  try {
    localStorage.setItem(CHIAVE_MEMORIA, layout)
  } catch {
    // Vale per questa sessione e basta.
  }
}

/** I vicini di ogni nodo, da entrambe le direzioni degli archi. */
function vicinato(edges: ArcoDaPosizionare[]): Map<string, string[]> {
  const out = new Map<string, string[]>()
  for (const arco of edges) {
    if (arco.src === arco.dst) continue
    const a = out.get(arco.src)
    if (a) a.push(arco.dst)
    else out.set(arco.src, [arco.dst])
    const b = out.get(arco.dst)
    if (b) b.push(arco.src)
    else out.set(arco.dst, [arco.src])
  }
  return out
}

function confronta(a: NodoDaPosizionare, b: NodoDaPosizionare): number {
  const oa = (a.ordine ?? '').toLowerCase()
  const ob = (b.ordine ?? '').toLowerCase()
  if (oa !== ob) return oa < ob ? -1 : 1
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
}

/** Quanti nodi in una riga prima di andare a capo, per il totale della mappa. */
function perRiga(totale: number): number {
  return Math.max(8, Math.min(20, Math.ceil(Math.sqrt(totale) * 1.8)))
}

/**
 * La vista gerarchica: fasce per livello, ordinate per baricentro.
 *
 * L'ordinamento fa quattro giri alternati (dall'alto in basso e ritorno):
 * ogni nodo si mette alla media delle posizioni dei suoi vicini nella fascia
 * accanto. È l'euristica di Sugiyama, ridotta all'essenziale e deterministica.
 */
function gerarchica(nodes: NodoDaPosizionare[], edges: ArcoDaPosizionare[]): Disposizione {
  const vicini = vicinato(edges)
  const perLivello = new Map<number, NodoDaPosizionare[]>()
  for (const nodo of nodes) {
    const lista = perLivello.get(nodo.level)
    if (lista) lista.push(nodo)
    else perLivello.set(nodo.level, [nodo])
  }
  const livelli = [...perLivello.keys()].sort((a, b) => a - b)
  const righe = livelli.map((l) => [...(perLivello.get(l) ?? [])].sort(confronta))

  // La posizione relativa (0..1) di ogni nodo nella sua fascia.
  const rel = new Map<string, number>()
  const aggiorna = (riga: NodoDaPosizionare[]) =>
    riga.forEach((n, i) => rel.set(n.id, riga.length === 1 ? 0.5 : i / (riga.length - 1)))
  righe.forEach(aggiorna)

  const riordina = (riga: NodoDaPosizionare[], guida: Set<string>) => {
    const chiave = new Map<string, number>()
    riga.forEach((n) => {
      const note = (vicini.get(n.id) ?? []).filter((v) => guida.has(v)).map((v) => rel.get(v) ?? 0.5)
      chiave.set(n.id, note.length ? note.reduce((s, v) => s + v, 0) / note.length : rel.get(n.id) ?? 0.5)
    })
    riga.sort((a, b) => (chiave.get(a.id)! - chiave.get(b.id)!) || confronta(a, b))
    aggiorna(riga)
  }

  for (let giro = 0; giro < 4; giro++) {
    for (let i = 1; i < righe.length; i++) riordina(righe[i], new Set(righe[i - 1].map((n) => n.id)))
    for (let i = righe.length - 2; i >= 0; i--) riordina(righe[i], new Set(righe[i + 1].map((n) => n.id)))
  }

  const massimo = perRiga(nodes.length)
  const posizioni = new Map<string, Posizione>()
  const fasce: Fascia[] = []
  let y = 0
  righe.forEach((riga, indice) => {
    const quante = Math.ceil(riga.length / massimo)
    const y0 = y
    for (let r = 0; r < quante; r++) {
      const pezzo = riga.slice(r * massimo, (r + 1) * massimo)
      // Le righe a capo si sfalsano di mezzo passo: così un arco che scende
      // dalla riga sopra non passa esattamente sopra un nodo di quella sotto.
      const sfalso = r % 2 === 1 ? PASSO_X / 2 : 0
      pezzo.forEach((nodo, i) => {
        posizioni.set(nodo.id, {
          x: (i - (pezzo.length - 1) / 2) * PASSO_X + sfalso,
          y: y + r * PASSO_RIGA,
        })
      })
    }
    const y1 = y + (quante - 1) * PASSO_RIGA
    fasce.push({ livello: livelli[indice], y0, y1 })
    y = y1 + PASSO_FASCIA
  })
  return { posizioni, fasce }
}

/**
 * La vista radiale: anelli per distanza, spicchi per ramo.
 */
function radiale(
  nodes: NodoDaPosizionare[],
  edges: ArcoDaPosizionare[],
  distanza?: Map<string, number>,
): Disposizione {
  const vicini = vicinato(edges)
  // Senza un nodo scelto, gli anelli sono le fasce del criterio, dal primo
  // livello al centro verso l'esterno: una «cipolla» (in topologia: il
  // perimetro al centro, lo storage fuori). Con un nodo scelto, la distanza
  // in salti da lui.
  let anelloDi: (n: NodoDaPosizionare) => number
  if (distanza && distanza.size) {
    const oltre = Math.max(0, ...distanza.values()) + 1
    anelloDi = (n) => distanza.get(n.id) ?? oltre
  } else {
    const livelli = [...new Set(nodes.map((n) => n.level))].sort((a, b) => a - b)
    const rango = new Map(livelli.map((l, i) => [l, i]))
    anelloDi = (n) => rango.get(n.level) ?? 0
  }

  const perAnello = new Map<number, NodoDaPosizionare[]>()
  for (const nodo of nodes) {
    const k = anelloDi(nodo)
    const lista = perAnello.get(k)
    if (lista) lista.push(nodo)
    else perAnello.set(k, [nodo])
  }
  const anelli = [...perAnello.keys()].sort((a, b) => a - b)
  const anelloDiId = new Map<string, number>()
  for (const nodo of nodes) anelloDiId.set(nodo.id, anelloDi(nodo))

  // L'albero degli spicchi: ogni nodo sceglie come genitore un vicino
  // dell'anello più interno disponibile; chi non ne ha pende dal centro.
  // Ogni nodo riceve uno spicchio di cerchio proporzionale alle foglie che
  // porta, e i suoi figli si dividono il suo spicchio: il cerchio si riempie
  // tutto e ogni ramo resta «sotto» il suo genitore.
  const ordinati = [...nodes].sort(
    (a, b) => anelloDiId.get(a.id)! - anelloDiId.get(b.id)! || confronta(a, b),
  )
  const figli = new Map<string, NodoDaPosizionare[]>()
  const radici: NodoDaPosizionare[] = []
  for (const nodo of ordinati) {
    const mio = anelloDiId.get(nodo.id)!
    let genitore: string | undefined
    let migliore = Infinity
    for (const v of vicini.get(nodo.id) ?? []) {
      const suo = anelloDiId.get(v)
      if (suo === undefined || suo >= mio) continue
      // Il più vicino di anello; a parità, il primo in ordine: deterministico.
      if (mio - suo < migliore || (mio - suo === migliore && genitore !== undefined && v < genitore)) {
        migliore = mio - suo
        genitore = v
      }
    }
    if (genitore === undefined) radici.push(nodo)
    else {
      const lista = figli.get(genitore)
      if (lista) lista.push(nodo)
      else figli.set(genitore, [nodo])
    }
  }
  const peso = new Map<string, number>()
  const pesa = (nodo: NodoDaPosizionare): number => {
    const somma = (figli.get(nodo.id) ?? []).reduce((s, f) => s + pesa(f), 0)
    const p = Math.max(1, somma)
    peso.set(nodo.id, p)
    return p
  }
  const totale = radici.reduce((s, r) => s + pesa(r), 0) || 1
  const spicchio = new Map<string, number>()
  const assegna = (lista: NodoDaPosizionare[], da: number, ampiezza: number, somma: number) => {
    let a = da
    for (const nodo of lista) {
      const quota = (ampiezza * peso.get(nodo.id)!) / somma
      spicchio.set(nodo.id, a + quota / 2)
      const sotto = figli.get(nodo.id) ?? []
      if (sotto.length) assegna(sotto, a, quota, peso.get(nodo.id)!)
      a += quota
    }
  }
  assegna(radici, -Math.PI / 2, 2 * Math.PI, totale)

  const posizioni = new Map<string, Posizione>()
  const angolo = new Map<string, number>()
  const guide: Anello[] = []
  const perSalti = !!(distanza && distanza.size)
  let raggio = 0

  anelli.forEach((k, indice) => {
    const lista = [...(perAnello.get(k) ?? [])].sort(confronta)
    const minimo = (lista.length * PASSO_X * 0.9) / (2 * Math.PI)
    if (indice === 0) {
      raggio = lista.length === 1 ? 0 : Math.max(PASSO_ANELLO * 0.6, minimo)
    } else {
      raggio = Math.max(raggio + PASSO_ANELLO, minimo)
    }
    const desiderato = new Map<string, number>()
    lista.forEach((n) => {
      // Chi pende da più nodi dell'anello subito dentro (un datastore da
      // dodici VM) si mette in mezzo a loro — media circolare, con seno e
      // coseno: quella aritmetica di 350° e 10° darebbe il lato opposto.
      // Gli altri stanno al centro del loro spicchio.
      let piuDentro = -Infinity
      for (const v of vicini.get(n.id) ?? []) {
        const suo = anelloDiId.get(v)
        if (suo !== undefined && suo < k && angolo.has(v)) piuDentro = Math.max(piuDentro, suo)
      }
      let sx = 0
      let sy = 0
      let quanti = 0
      for (const v of vicini.get(n.id) ?? []) {
        if (anelloDiId.get(v) !== piuDentro) continue
        const a = angolo.get(v)
        if (a === undefined) continue
        sx += Math.cos(a)
        sy += Math.sin(a)
        quanti++
      }
      const medio = Math.atan2(sy, sx)
      // Riportato vicino allo spicchio, così l'ordinamento non salta il giro.
      const base = spicchio.get(n.id) ?? 0
      const vicino = medio + 2 * Math.PI * Math.round((base - medio) / (2 * Math.PI))
      desiderato.set(n.id, quanti >= 2 && (sx !== 0 || sy !== 0) ? vicino : base)
    })
    lista.sort((a, b) => (desiderato.get(a.id)! - desiderato.get(b.id)!) || confronta(a, b))
    // Ognuno parte dal centro del suo spicchio, poi si allontana dai vicini di
    // anello quanto basta per non toccarli.
    const n = lista.length
    const ang = lista.map((x) => desiderato.get(x.id)!)
    const gap = raggio > 0 ? Math.min((2 * Math.PI) / Math.max(1, n), (PASSO_X * 0.92) / raggio) : 0
    for (let passata = 0; passata < 80 && n > 1; passata++) {
      let mosso = false
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n
        let diff = ang[j] - ang[i]
        if (j === 0) diff += 2 * Math.PI
        while (diff < -Math.PI) diff += 2 * Math.PI
        if (diff >= gap - 1e-6) continue
        const spinta = (gap - diff) / 2
        ang[i] -= spinta
        ang[j] += spinta
        mosso = true
      }
      if (!mosso) break
    }
    lista.forEach((nodo, i) => {
      angolo.set(nodo.id, ang[i])
      posizioni.set(nodo.id, { x: raggio * Math.cos(ang[i]), y: raggio * Math.sin(ang[i]) })
    })
    if (raggio > 0) {
      // Il nome dell'anello va nel varco più ampio fra i suoi nodi: sopra un
      // nodo non si leggerebbe.
      let varco = -Math.PI / 2
      let largo = -1
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n
        let diff = n === 1 ? 2 * Math.PI : ang[j] - ang[i]
        if (n > 1 && j === 0) diff += 2 * Math.PI
        if (diff > largo) {
          largo = diff
          varco = ang[i] + diff / 2
        }
      }
      const nome = perSalti ? { salti: k } : { livello: lista[0].level }
      guide.push({ raggio, angoloNome: varco, ...nome })
    }
  })
  return { posizioni, fasce: [], anelli: guide }
}

/**
 * La vista organica: forze (Fruchterman–Reingold) a partire dalla gerarchica,
 * con una molla verso la fascia. Deterministica: stesso punto di partenza,
 * stessi giri, stessa forma.
 */
function organica(nodes: NodoDaPosizionare[], edges: ArcoDaPosizionare[]): Disposizione {
  const base = gerarchica(nodes, edges)
  const ids = nodes.map((n) => n.id).sort()
  const n = ids.length
  if (n < 2) return { posizioni: base.posizioni, fasce: [] }
  const indice = new Map(ids.map((id, i) => [id, i]))
  const x = new Float64Array(n)
  const y = new Float64Array(n)
  const yFascia = new Float64Array(n)
  ids.forEach((id, i) => {
    const p = base.posizioni.get(id)!
    x[i] = p.x
    y[i] = p.y
    yFascia[i] = p.y
  })
  const archi: [number, number][] = []
  for (const e of edges) {
    const a = indice.get(e.src)
    const b = indice.get(e.dst)
    if (a !== undefined && b !== undefined && a !== b) archi.push(a < b ? [a, b] : [b, a])
  }
  // In ordine fisso: le somme in virgola mobile dipendono dall'ordine, e in
  // trecento giri una differenza all'ultima cifra diventa un'altra forma. La
  // mappa deve dipendere dalla rete, non da come l'API elenca gli archi.
  archi.sort((p, q) => p[0] - q[0] || p[1] - q[1])
  const k = 135
  const giri = n <= 80 ? 350 : n <= 250 ? 200 : n <= 500 ? 90 : 45
  const dx = new Float64Array(n)
  const dy = new Float64Array(n)
  let temperatura = 90
  const raffredda = temperatura / (giri + 1)

  for (let g = 0; g < giri; g++) {
    dx.fill(0)
    dy.fill(0)
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        let ddx = x[i] - x[j]
        let ddy = y[i] - y[j]
        let d2 = ddx * ddx + ddy * ddy
        if (d2 < 0.01) {
          // Due nodi sovrapposti: si separano in una direzione fissa, non
          // casuale — il determinismo vale anche qui.
          ddx = 0.1 * ((i % 7) - 3)
          ddy = 0.1 * ((j % 5) - 2)
          d2 = 0.02
        }
        const f = (k * k) / d2
        dx[i] += ddx * f
        dy[i] += ddy * f
        dx[j] -= ddx * f
        dy[j] -= ddy * f
      }
    }
    for (const [a, b] of archi) {
      const ddx = x[a] - x[b]
      const ddy = y[a] - y[b]
      const d = Math.sqrt(ddx * ddx + ddy * ddy) || 0.01
      const f = d / k
      dx[a] -= ddx * f
      dy[a] -= ddy * f
      dx[b] += ddx * f
      dy[b] += ddy * f
    }
    for (let i = 0; i < n; i++) {
      // La molla verso la fascia: il criterio resta leggibile anche qui.
      dy[i] += (yFascia[i] - y[i]) * 0.35
      dx[i] -= x[i] * 0.02
      const d = Math.sqrt(dx[i] * dx[i] + dy[i] * dy[i]) || 1
      const passo = Math.min(d, temperatura)
      x[i] += (dx[i] / d) * passo
      y[i] += (dy[i] / d) * passo
    }
    temperatura -= raffredda
  }

  // Ultima passata: nessun nodo sopra un altro.
  const minima = 104
  for (let passata = 0; passata < 40; passata++) {
    let mosso = false
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const ddx = x[j] - x[i]
        const ddy = y[j] - y[i]
        const d = Math.sqrt(ddx * ddx + ddy * ddy)
        if (d >= minima) continue
        const spinta = (minima - d) / 2 + 0.5
        const ux = d > 0.01 ? ddx / d : 1
        const uy = d > 0.01 ? ddy / d : 0
        x[i] -= ux * spinta
        y[i] -= uy * spinta
        x[j] += ux * spinta
        y[j] += uy * spinta
        mosso = true
      }
    }
    if (!mosso) break
  }

  const posizioni = new Map<string, Posizione>()
  ids.forEach((id, i) => posizioni.set(id, { x: x[i], y: y[i] }))
  return { posizioni, fasce: [] }
}

export function posiziona(
  nodes: NodoDaPosizionare[],
  edges: ArcoDaPosizionare[],
  layout: LayoutMappa,
  distanza?: Map<string, number>,
): Disposizione {
  if (nodes.length === 0) return { posizioni: new Map(), fasce: [] }
  if (layout === 'radiale') return radiale(nodes, edges, distanza)
  if (layout === 'organico') return organica(nodes, edges)
  return gerarchica(nodes, edges)
}
